import React, { useState } from 'react';
import { supabase } from '../supabaseClient';

// Haversine formula to calculate distance between two GPS points in meters
const getDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth radius in meters
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const Cart = ({ cartItems, onClose, onUpdateCart, restaurantId, tableNumber, onOrderPlaced, themeColor, restaurant }) => {
  const [ordering, setOrdering] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);

  const totalPrice = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

  // Step 1: Show location dialog before requesting permission
  const handlePlaceOrder = () => {
    if (cartItems.length === 0) return;
    const hasGeofence = restaurant?.latitude && restaurant?.longitude;
    if (hasGeofence) {
      setShowLocationDialog(true);
    } else {
      proceedWithOrder();
    }
  };

  // Step 2: After user confirms, request location and verify
  const proceedWithOrder = async () => {
    setShowLocationDialog(false);
    setOrdering(true);
    setGeoError(null);

    const hasGeofence = restaurant?.latitude && restaurant?.longitude;

    if (hasGeofence) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0
          });
        });

        // Check if location is precise enough
        if (position.coords.accuracy > 500) {
          setGeoError(isIOS
            ? 'Your location is not precise enough. On iPhone: Settings → Privacy → Location Services → Safari Websites → enable "Precise Location". Then refresh and try again.'
            : 'Your location is not precise enough. On Android: Settings → Location → turn ON "High Accuracy" or "Precise Location". Then try again.');
          setOrdering(false);
          return;
        }

        const distance = getDistanceMeters(
          position.coords.latitude,
          position.coords.longitude,
          restaurant.latitude,
          restaurant.longitude
        );

        const radius = restaurant.geofence_radius || 50;

        if (distance > radius) {
          setGeoError('You need to be at the restaurant to place an order. If you are at the restaurant, please enable Precise/High-Accuracy Location in your device settings and try again.');
          setOrdering(false);
          return;
        }
      } catch (err) {
        if (err.code === 1) {
          setGeoError(isIOS
            ? 'Location access denied. On iPhone: Go to Settings → Privacy → Location Services → Safari Websites → set to "While Using". Then refresh this page.'
            : 'Location access was denied. Please tap "Allow" when asked, and make sure Location is turned ON in your device Settings.');
        } else if (err.code === 2) {
          setGeoError(isIOS
            ? 'Could not detect your location. Make sure Location Services are ON in Settings → Privacy → Location Services.'
            : 'Could not detect your location. Please make sure Location is turned ON in your device Settings.');
        } else {
          setGeoError('Location request timed out. Please check your connection and try again.');
        }
        setOrdering(false);
        return;
      }
    }

    // --- PLACE ORDER ---
    try {
      const { data, error } = await supabase
        .from('orders')
        .insert([{
          restaurant_id: restaurantId,
          table_number: tableNumber || 'N/A',
          items: cartItems,
          total_price: totalPrice,
          status: 'Pending'
        }])
        .select()
        .single();

      if (error) throw error;

      onOrderPlaced(data.id);
      onUpdateCart([]);
      onClose();

    } catch (err) {
      alert("Failed to place order: " + err.message);
      setOrdering(false);
    }
  };

  const updateQuantity = (index, delta) => {
    const newCart = [...cartItems];
    newCart[index] = { ...newCart[index], quantity: newCart[index].quantity + delta };
    if (newCart[index].quantity <= 0) newCart.splice(index, 1);
    onUpdateCart(newCart);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
      <div className="bg-white w-full max-w-md h-full flex flex-col animate-slide-in-right">

        <div className="p-6 border-b bg-gray-50 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Your Order</h2>
            <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-1 rounded mt-1 inline-block">
              📍 Table {tableNumber || "N/A"}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-500 font-bold text-xl">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {cartItems.length === 0 ? (
            <div className="text-center text-gray-400 mt-20">Cart is empty</div>
          ) : (
            cartItems.map((item, index) => (
              <div key={index} className="flex justify-between items-center border-b pb-4">
                <div>
                  <h4 className="font-bold text-gray-900">{item.name}</h4>
                  <p className="text-sm text-gray-500">₹{item.price * item.quantity}</p>
                </div>
                <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-1">
                  <button onClick={() => updateQuantity(index, -1)} className="w-8 h-8 flex items-center justify-center font-bold text-gray-600 bg-white rounded shadow-sm">-</button>
                  <span className="font-bold text-sm w-4 text-center text-gray-900">{item.quantity}</span>
                  <button onClick={() => updateQuantity(index, 1)} className="w-8 h-8 flex items-center justify-center font-bold text-gray-600 bg-white rounded shadow-sm">+</button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-6 border-t bg-gray-50">
          {/* Geofence Error */}
          {geoError && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs font-medium mb-4 flex items-start gap-2">
              <span className="text-base mt-[-2px]">📍</span>
              <span>{geoError}</span>
            </div>
          )}

          <div className="flex justify-between text-xl font-bold mb-6">
            <span>Total</span>
            <span>₹{totalPrice}</span>
          </div>
          <button
            onClick={handlePlaceOrder}
            disabled={cartItems.length === 0 || ordering}
            className="w-full py-4 text-white font-bold rounded-xl shadow-lg hover:opacity-90 transition-all flex justify-center items-center gap-2"
            style={{ backgroundColor: themeColor }}
          >
            {ordering ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : '✅ Place Order'}
          </button>
        </div>
      </div>

      {/* LOCATION PERMISSION DIALOG */}
      {showLocationDialog && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white text-center">
              <div className="text-4xl mb-3">📍</div>
              <h3 className="text-xl font-bold">Location Required</h3>
              <p className="text-white/80 text-sm mt-2">We need your precise location to verify you're at the restaurant.</p>
            </div>

            <div className="p-5">
              {isIOS ? (
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <p className="font-bold text-gray-900 text-sm">Before tapping Continue:</p>
                  <ol className="text-xs text-gray-600 space-y-1.5 list-decimal list-inside">
                    <li>Open <strong>Settings → Privacy → Location Services</strong></li>
                    <li>Make sure Location Services is <strong>ON</strong></li>
                    <li>Scroll to <strong>Safari Websites</strong> → set to <strong>"While Using"</strong></li>
                    <li>Enable <strong>"Precise Location"</strong></li>
                  </ol>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <p className="font-bold text-gray-900 text-sm">Before tapping Continue:</p>
                  <ol className="text-xs text-gray-600 space-y-1.5 list-decimal list-inside">
                    <li>Open <strong>Settings → Location</strong></li>
                    <li>Make sure Location is <strong>ON</strong></li>
                    <li>Set mode to <strong>"High Accuracy"</strong> or <strong>"Precise"</strong></li>
                    <li>When the browser asks, tap <strong>"Allow"</strong> and select <strong>"Precise"</strong></li>
                  </ol>
                </div>
              )}
            </div>

            <div className="p-4 pt-0 flex gap-3">
              <button onClick={() => setShowLocationDialog(false)} className="flex-1 py-3 bg-gray-100 text-gray-600 font-bold rounded-xl text-sm hover:bg-gray-200 transition-colors">
                Cancel
              </button>
              <button onClick={proceedWithOrder} className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-xl text-sm hover:bg-blue-700 transition-colors shadow-lg">
                Continue →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;